import {
  Badge,
  Button,
  EmptyState,
  ErrorBanner,
} from "frontend-kit/components/index.js";
import { achievementNftConfigured } from "./chain-config.js";
import { submitAchievementClaim } from "./achievement-claim.js";
import { duskAmountNode } from "./dusk-amount.js";
import {
  getAchievementStats,
  getClaimProof,
  getOnboardRoot,
  getPublishStatus,
  loadBackendConfig,
  PM_ONBOARD_VERIFIED,
} from "./gateway-api.js";
import { isParticipantReady } from "./participant-store.js";
import { formatCountdown, msUntil, claimBlockedByPendingChain, resolveStreakEpoch } from "./publish-countdown.js";
import { loadTradingKey } from "./trading-key-store.js";
import { traderCommitmentFromPkHex } from "./trader-crypto.js";
import { maskCommitment } from "./privacy-copy.js";
import {
  ACHIEVEMENT_ART,
  achievementImageUrl,
} from "./nft-art.js";
import { collapseHeldTypes } from "./trader-gallery.js";

const STREAK_CLAIM_MIN = 3;
const POLL_MS = 30_000;
const CUTOVER_MSG =
  "Achievement NFT claim is not live until Phase G cutover - achievementNft must be pinned in contracts.testnet.json.";
const PUBLISH_BATCH_COPY =
  "New verified traders are batched about every 15 minutes (and within an hour at latest) so we don't spam the chain.";
const PUBLISH_STATIC_WAIT_COPY =
  "Publishing usually happens within about 15 minutes, and within an hour at latest.";

/**
 * @param {HTMLElement} parent
 * @param {object | null | undefined} publishStatus
 * @param {number} nowMs
 */
function appendPublishWaitUi(parent, publishStatus, nowMs) {
  if (publishStatus?.pending_chain) {
    parent.append(
      Object.assign(document.createElement("p"), {
        className: "hint achievements-publish-wait",
        textContent: "Snapshot ready - confirming on-chain…",
      })
    );
    return;
  }

  if (publishStatus?.dirty && publishStatus.next_publish_at) {
    const remain = msUntil(publishStatus.next_publish_at, nowMs);
    parent.append(
      Object.assign(document.createElement("p"), {
        className: "hint achievements-publish-countdown",
        textContent:
          remain != null && remain > 0
            ? `Publishes in ${formatCountdown(remain)}`
            : "Publishing soon…",
      }),
      Object.assign(document.createElement("p"), {
        className: "hint",
        textContent: PUBLISH_BATCH_COPY,
      })
    );
    return;
  }

  parent.append(
    Object.assign(document.createElement("p"), {
      className: "hint",
      textContent: PUBLISH_STATIC_WAIT_COPY,
    })
  );
}

function streakEpochFromUrl() {
  const raw = new URLSearchParams(location.search).get("streakEpoch");
  if (!raw) return 1;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
}

function walletConnected(wallet) {
  const state = wallet?.state;
  return Boolean(state?.authorized && state.profiles?.length);
}

function resolveCommitmentHex(key) {
  if (key?.commitment_hex) return key.commitment_hex;
  if (key?.pk_hex) return traderCommitmentFromPkHex(key.pk_hex);
  return null;
}

/**
 * @param {{ wallet: object, log?: Function, onOpenSettings?: Function }} opts
 */
export async function renderAchievementsPage({ wallet, log, onOpenSettings }) {
  const root = document.createElement("div");
  root.className = "achievements-page";

  const header = document.createElement("div");
  header.className = "achievements-header";
  const h1 = document.createElement("h1");
  h1.textContent = "Achievements";
  header.append(h1);
  root.append(header);

  const journey = document.createElement("p");
  journey.className = "hint achievements-journey";
  journey.textContent =
    "Win streaks → claim on Leaderboard or Achievements → post your participation bond in Settings → create events from Create.";
  root.append(journey);

  const onboardStatusHost = document.createElement("div");
  onboardStatusHost.className = "achievements-status-host achievements-onboard-status-host";
  root.append(onboardStatusHost);

  const statusHost = document.createElement("div");
  statusHost.className = "achievements-status-host";
  root.append(statusHost);

  const bodyHost = document.createElement("div");
  bodyHost.className = "achievements-body-host";
  root.append(bodyHost);

  const galleryHost = document.createElement("section");
  galleryHost.className = "achievements-gallery-host";
  galleryHost.hidden = true;
  const galleryTitle = document.createElement("h2");
  galleryTitle.className = "achievements-section-title";
  galleryTitle.textContent = "Earned NFTs";
  galleryHost.append(galleryTitle);
  const galleryBody = document.createElement("div");
  galleryHost.append(galleryBody);
  root.append(galleryHost);

  if (!walletConnected(wallet) || !loadTradingKey()?.sk_hex) {
    bodyHost.append(
      EmptyState({
        title: "Connect wallet and trading key",
        description:
          "Link your Moonlight wallet and derive a trading key in Account settings before claiming achievements.",
      }),
      Button({
        label: "Open account settings",
        variant: "primary",
        onClick: () => onOpenSettings?.() || (location.hash = "#/settings"),
      })
    );
    return root;
  }

  const key = loadTradingKey();
  const commitmentHex = resolveCommitmentHex(key);
  if (!commitmentHex) {
    bodyHost.append(
      EmptyState({
        title: "Trading key incomplete",
        description: "Re-derive your trading key in Account settings.",
      }),
      Button({
        label: "Open account settings",
        variant: "primary",
        onClick: () => onOpenSettings?.() || (location.hash = "#/settings"),
      })
    );
    return root;
  }

  const cfg = loadBackendConfig();
  const streakEpochUrl = streakEpochFromUrl();
  const nftLive = achievementNftConfigured();
  const participantReady = isParticipantReady();

  if (!nftLive) {
    statusHost.append(ErrorBanner({ message: CUTOVER_MSG }));
  }

  const onboardCard = document.createElement("section");
  onboardCard.className = "achievements-card dt-card achievements-onboard-card";
  onboardCard.hidden = true;
  bodyHost.append(onboardCard);

  const card = document.createElement("section");
  card.className = "achievements-card dt-card";
  bodyHost.append(card);

  let onboardRoot = null;
  let onboardPublishStatus = null;
  let onboardClaimReady = false;
  let onboardLoading = true;
  let onboardClaiming = false;
  let onboardClaimSuccess = false;

  let stats = null;
  let streakPublishStatus = null;
  let streakEpoch = streakEpochUrl;
  let streakClaimReady = false;
  let err = null;
  let loading = true;
  let claiming = false;
  let claimSuccess = false;
  let countdownNow = Date.now();
  let disposed = false;

  async function probeClaimProof(epoch, type) {
    if (!epoch) return false;
    try {
      await getClaimProof(cfg, commitmentHex, { epoch, type });
      return true;
    } catch {
      return false;
    }
  }

  function showOnboardCard() {
    return participantReady || onboardRoot || onboardLoading;
  }

  function countdownActive() {
    return (
      (onboardPublishStatus?.dirty && onboardPublishStatus?.next_publish_at) ||
      (streakPublishStatus?.dirty && streakPublishStatus?.next_publish_at)
    );
  }

  function paintCountdownIfNeeded() {
    if (!countdownActive()) return;
    if (!onboardClaimReady && showOnboardCard()) paintOnboardCard();
    const claimable = stats && (stats.qualifies_streak_3 || stats.streak >= STREAK_CLAIM_MIN);
    if (claimable && !streakClaimReady) paintCard();
  }

  function paintGallery() {
    const held = stats?.held_types ?? [];
    galleryHost.hidden = !held.length;
    galleryBody.replaceChildren();
    if (!held.length) return;
    const grid = document.createElement("div");
    grid.className = "trader-gallery achievements-gallery";
    for (const item of collapseHeldTypes(held)) {
      const card = document.createElement("article");
      card.className = "trader-gallery-card dt-card";
      const art = ACHIEVEMENT_ART[item.achievement_type];
      const img = document.createElement("img");
      img.className = "trader-gallery-art";
      img.alt = art?.name || `Achievement ${item.achievement_type}`;
      img.loading = "lazy";
      if (art) img.src = achievementImageUrl(item.achievement_type, location.origin);
      const title = document.createElement("h3");
      title.className = "trader-gallery-title";
      title.textContent = art?.name || `Type ${item.achievement_type}`;
      card.append(img, title);
      if (item.count > 1) {
        card.append(Badge({ variant: "neutral", label: `×${item.count}` }));
      }
      grid.append(card);
    }
    galleryBody.append(grid);
  }

  function paintOnboardCard() {
    onboardCard.replaceChildren();
    onboardCard.hidden = !showOnboardCard();
    if (onboardCard.hidden) return;

    const title = document.createElement("h2");
    title.className = "achievements-section-title";
    title.textContent = "Verified trader";
    onboardCard.append(title);

    if (onboardLoading) {
      onboardCard.append(
        Object.assign(document.createElement("p"), {
          className: "hint",
          textContent: "Loading onboard claim…",
        })
      );
      return;
    }

    const meta = document.createElement("div");
    meta.className = "achievements-meta";

    const blurb = document.createElement("p");
    blurb.className = "hint";
    blurb.textContent =
      "Soft-onboarded traders (verified wallet + linked trading key) can claim a lab/test NFT. It does not unlock Create - only the streak achievement does.";
    meta.append(blurb);

    if (onboardRoot) {
      const epochHint = document.createElement("p");
      epochHint.className = "hint";
      epochHint.textContent = `Onboard epoch ${onboardRoot.epoch} (snapshot at publish time).`;
      meta.append(epochHint);
    }

    onboardCard.append(meta);

    const claimRow = document.createElement("div");
    claimRow.className = "achievements-claim-row state-row";

    if (onboardClaimReady) {
      claimRow.append(Badge({ variant: "success", label: "Eligible to claim" }));

      if (nftLive) {
        claimRow.append(
          Button({
            label: onboardClaiming ? "Claiming…" : "Claim test NFT",
            variant: "primary",
            disabled: onboardClaiming || onboardClaimSuccess,
            onClick: async () => {
              onboardClaiming = true;
              onboardClaimSuccess = false;
              onboardStatusHost.replaceChildren();
              paintOnboardCard();
              try {
                await submitAchievementClaim({
                  commitmentHex,
                  epoch: onboardRoot.epoch,
                  achievementType: PM_ONBOARD_VERIFIED,
                  log,
                });
                onboardClaimSuccess = true;
                log?.("onboard achievement claim submitted");
                onboardStatusHost.append(
                  Object.assign(document.createElement("p"), {
                    className: "hint achievements-claim-ok",
                    textContent:
                      "Test NFT minted to your Moonlight wallet. It does not unlock Create - claim a streak achievement and post your bond for that.",
                  })
                );
              } catch (e) {
                onboardStatusHost.append(ErrorBanner({ message: e.message || String(e) }));
              } finally {
                onboardClaiming = false;
                paintOnboardCard();
              }
            },
          })
        );
      } else {
        const cutover = document.createElement("p");
        cutover.className = "hint";
        cutover.textContent = "Claim unlocks after Phase G cutover.";
        claimRow.append(cutover);
      }
    } else {
      if (participantReady) {
        claimRow.append(Badge({ variant: "pending", label: "Waiting for publish" }));
        const waitHost = document.createElement("div");
        waitHost.className = "achievements-publish-wait-host";
        appendPublishWaitUi(waitHost, onboardPublishStatus, countdownNow);
        meta.append(waitHost);
      } else {
        claimRow.append(Badge({ variant: "neutral", label: "Verify + link to claim" }));
        const hint = document.createElement("p");
        hint.className = "hint";
        hint.textContent =
          "Verify your wallet and link your trading key in Account settings before claiming.";
        meta.append(hint);
        claimRow.append(
          Button({
            label: "Open account settings",
            variant: "secondary",
            onClick: () => onOpenSettings?.() || (location.hash = "#/settings"),
          })
        );
      }
    }

    onboardCard.append(claimRow);

    if (onboardClaimSuccess) {
      const next = document.createElement("p");
      next.className = "hint";
      next.textContent =
        "This NFT is for testing the claim path only. Create stays locked until you hold the streak achievement and post your participation bond.";
      onboardCard.append(next);
    }
  }

  function paintCard() {
    card.replaceChildren();

    if (loading) {
      card.append(
        Object.assign(document.createElement("p"), {
          className: "hint",
          textContent: "Loading streak stats…",
        })
      );
      return;
    }

    if (err) {
      card.append(ErrorBanner({ message: err }));
      return;
    }

    if (!stats) {
      card.append(
        EmptyState({
          title: "No streak stats yet",
          description:
            "Trade on finalized markets to build a streak. Stats appear once the indexer has processed your activity.",
        })
      );
      return;
    }

    const meta = document.createElement("div");
    meta.className = "achievements-meta";

    const traderRow = document.createElement("p");
    traderRow.className = "hint";
    traderRow.textContent = `Trader ${maskCommitment(commitmentHex.slice(0, 8))}`;
    meta.append(traderRow);

    const streakRow = document.createElement("div");
    streakRow.className = "state-row achievements-streak-row";
    const streakLabel = document.createElement("span");
    streakLabel.className = "achievements-streak-label";
    streakLabel.textContent = `Current streak: ${stats.streak}`;
    streakRow.append(streakLabel);
    if (stats.streak >= STREAK_CLAIM_MIN) {
      streakRow.append(Badge({ variant: "pending", label: "3+ wins" }));
    }
    meta.append(streakRow);

    const detail = document.createElement("p");
    detail.className = "hint";
    detail.textContent = `${stats.total_correct} correct · ${stats.total_markets} markets · buy vol `;
    detail.append(duskAmountNode(stats.buy_volume_lux));
    meta.append(detail);

    const epochHint = document.createElement("p");
    epochHint.className = "hint";
    epochHint.textContent = `Claim epoch ${streakEpoch} (streak snapshot at publish time).`;
    meta.append(epochHint);

    card.append(meta);

    const claimable = stats.qualifies_streak_3 || stats.streak >= STREAK_CLAIM_MIN;
    const claimRow = document.createElement("div");
    claimRow.className = "achievements-claim-row state-row";

    if (claimable && streakClaimReady) {
      claimRow.append(Badge({ variant: "success", label: "Eligible to claim" }));

      if (nftLive) {
        claimRow.append(
          Button({
            label: claiming ? "Claiming…" : "Claim NFT",
            variant: "primary",
            disabled: claiming || claimSuccess,
            onClick: async () => {
              claiming = true;
              claimSuccess = false;
              statusHost.replaceChildren();
              paintCard();
              try {
                await submitAchievementClaim({
                  commitmentHex,
                  epoch: streakEpoch,
                  log,
                });
                claimSuccess = true;
                log?.("achievement claim submitted");
                statusHost.append(
                  Object.assign(document.createElement("p"), {
                    className: "hint achievements-claim-ok",
                    textContent:
                      "NFT minted to your Moonlight wallet. Post your participation bond next, then create events.",
                  })
                );
                const settingsLink = document.createElement("a");
                settingsLink.href = "#/settings";
                settingsLink.className = "link-btn";
                settingsLink.textContent = "Open Account settings (bond)";
                settingsLink.addEventListener("click", (e) => {
                  e.preventDefault();
                  onOpenSettings?.() || (location.hash = "#/settings");
                });
                statusHost.append(settingsLink);
              } catch (e) {
                statusHost.append(ErrorBanner({ message: e.message || String(e) }));
              } finally {
                claiming = false;
                paintCard();
              }
            },
          })
        );
      } else {
        const cutover = document.createElement("p");
        cutover.className = "hint";
        cutover.textContent = "Eligible - claim unlocks after Phase G cutover.";
        claimRow.append(cutover);
      }
    } else if (claimable) {
      claimRow.append(Badge({ variant: "pending", label: "Waiting for publish" }));
      const waitHost = document.createElement("div");
      waitHost.className = "achievements-publish-wait-host";
      appendPublishWaitUi(waitHost, streakPublishStatus, countdownNow);
      meta.append(waitHost);
    } else {
      claimRow.append(Badge({ variant: "neutral", label: "Not eligible yet" }));
    }

    card.append(claimRow);

    if (claimSuccess) {
      const next = document.createElement("p");
      next.className = "hint";
      next.textContent =
        "When your NFT right is active on-chain, Create appears in the nav. Post your bond in Settings if the form asks for it.";
      card.append(next);
    }
  }

  async function load({ initial = false } = {}) {
    if (initial) {
      loading = true;
      onboardLoading = true;
      err = null;
      paintOnboardCard();
      paintCard();
    }
    try {
      const [
        statsResult,
        onboardResult,
        onboardStatusResult,
        streakStatusResult,
      ] = await Promise.allSettled([
        getAchievementStats(cfg, commitmentHex),
        getOnboardRoot(cfg),
        getPublishStatus(cfg, "onboard"),
        getPublishStatus(cfg, "streak"),
      ]);

      if (statsResult.status === "fulfilled") {
        stats = statsResult.value;
        log?.(`achievements stats streak=${stats.streak}`);
      } else {
        const msg = statsResult.reason?.message || String(statsResult.reason);
        if (msg.includes("404") || /unknown commitment/i.test(msg)) {
          stats = null;
          err = null;
        } else {
          stats = null;
          err = msg;
          log?.(`achievements error: ${err}`);
        }
      }

      if (onboardResult.status === "fulfilled") {
        onboardRoot = onboardResult.value;
        if (onboardRoot) {
          log?.(`onboard root epoch=${onboardRoot.epoch}`);
        }
      } else {
        onboardRoot = null;
        log?.(`onboard root error: ${onboardResult.reason?.message || onboardResult.reason}`);
      }

      onboardPublishStatus =
        onboardStatusResult.status === "fulfilled" ? onboardStatusResult.value : null;
      streakPublishStatus =
        streakStatusResult.status === "fulfilled" ? streakStatusResult.value : null;
      streakEpoch = resolveStreakEpoch(streakPublishStatus, streakEpochUrl);

      if (onboardStatusResult.status === "rejected") {
        log?.(
          `onboard publish-status error: ${onboardStatusResult.reason?.message || onboardStatusResult.reason}`
        );
      }
      if (streakStatusResult.status === "rejected") {
        log?.(
          `streak publish-status error: ${streakStatusResult.reason?.message || streakStatusResult.reason}`
        );
      }

      const onboardEpoch = onboardRoot?.epoch;
      const onboardBlocked =
        onboardEpoch &&
        claimBlockedByPendingChain(onboardPublishStatus, onboardEpoch);
      onboardClaimReady =
        onboardRoot &&
        !onboardBlocked &&
        (await probeClaimProof(onboardEpoch, PM_ONBOARD_VERIFIED));

      const claimable =
        stats && (stats.qualifies_streak_3 || stats.streak >= STREAK_CLAIM_MIN);
      const streakBlocked = claimBlockedByPendingChain(streakPublishStatus, streakEpoch);
      streakClaimReady =
        claimable && !streakBlocked && (await probeClaimProof(streakEpoch, 1));
    } finally {
      if (initial) {
        loading = false;
        onboardLoading = false;
      }
      countdownNow = Date.now();
      paintOnboardCard();
      paintCard();
      paintGallery();
    }
  }

  let pollTimer;
  let tickTimer;

  pollTimer = setInterval(() => {
    if (disposed || !root.isConnected) {
      disposed = true;
      clearInterval(pollTimer);
      clearInterval(tickTimer);
      return;
    }
    void load();
  }, POLL_MS);

  tickTimer = setInterval(() => {
    if (disposed || !root.isConnected) {
      disposed = true;
      clearInterval(pollTimer);
      clearInterval(tickTimer);
      return;
    }
    if (!countdownActive()) return;
    countdownNow = Date.now();
    paintCountdownIfNeeded();
  }, 1000);

  await load({ initial: true });
  return root;
}
